import type { InsufficientInventoryDetails, MarketplaceErrorCode as MarketplaceErrorCodeWire } from "@/generated/api/types.gen"
import { errorBodyMessage, errorCode, errorStatus, marketplaceErrorBody } from "@/platform/api/errors"

export const MarketplaceErrorCode = {
  CartNotFound: "marketplace.cart.not_found",
  CartMismatch: "marketplace.cart.mismatch",
  CartNotModifiable: "marketplace.cart.not_modifiable",
  CartStaleState: "marketplace.cart.stale_state",
  CartInvalidState: "marketplace.cart.invalid_state",
  CartInsufficientInventory: "marketplace.cart.insufficient_inventory",
  CartDiscountsInvalidated: "marketplace.cart.discounts_invalidated",
  CartInvalidQuantity: "marketplace.cart.invalid_quantity",
  StoreCreditInsufficient: "marketplace.store_credit.insufficient",

  CheckoutMissingAddress: "marketplace.checkout.missing_address",
  CheckoutMissingShippingMethods: "marketplace.checkout.missing_shipping_methods",
  CheckoutProductUnavailable: "marketplace.checkout.product_unavailable",

  CheckoutBlockedAccount: "marketplace.checkout.blocked_account",
  CheckoutBlockedOverduePayment: "marketplace.checkout.blocked_overdue_payment",
  CheckoutBlockedNotApproved: "marketplace.checkout.blocked_not_approved",
  CheckoutBlockedFraud: "marketplace.checkout.blocked_fraud",
  CheckoutBlockedRisk: "marketplace.checkout.blocked_risk",
  CheckoutBlockedLoanLimit: "marketplace.checkout.blocked_loan_limit",
  CheckoutBlockedNewUser: "marketplace.checkout.blocked_new_user",
  CheckoutBlockedCreditReduced: "marketplace.checkout.blocked_credit_reduced",

  CheckoutPaymentSessionRequired: "marketplace.checkout.payment_session_required",
  PaymentSessionMissingData: "marketplace.payment_session.missing_data",
  PaymentAmountMismatch: "marketplace.payment.amount_mismatch",
  PaymentAuthorizationInvalid: "marketplace.payment.authorization_invalid",
  PaymentAuthorizationExpired: "marketplace.payment.authorization_expired",
  PaymentIntentInvalid: "marketplace.payment.intent_invalid",
  PaymentIntentExpired: "marketplace.payment.intent_expired",

  LoanAlreadyExists: "marketplace.loan.already_exists",
  LoanInvalidState: "marketplace.loan.invalid_state",
  LoanCreditLimitExceeded: "marketplace.loan.credit_limit_exceeded",
} as const satisfies Record<string, MarketplaceErrorCodeWire>

export type MarketplaceErrorCode = (typeof MarketplaceErrorCode)[keyof typeof MarketplaceErrorCode]

export function getMarketplaceErrorCode(error: unknown): string | null {
  return errorCode(error)
}

export function isMarketplaceError(error: unknown, code: MarketplaceErrorCode): boolean {
  return errorCode(error) === code
}

export function getMarketplaceErrorStatus(error: unknown): number | null {
  return errorStatus(error)
}

interface ErrorCopy {
  title?: string
  body: string
}

const insufficientInventoryCopy = ({ available, requested }: InsufficientInventoryDetails): ErrorCopy =>
  available === 0
    ? { body: "Uno de los productos en tu carrito se agotó. Quítalo para continuar." }
    : { body: `Solo quedan ${available} unidades disponibles (intentaste ${requested}). Ajusta tu carrito.` }

export function getMarketplaceErrorCopy(error: unknown): ErrorCopy | null {
  const insufficientInventory = marketplaceErrorBody(error, MarketplaceErrorCode.CartInsufficientInventory)
  if (insufficientInventory) return insufficientInventoryCopy(insufficientInventory.details)

  if (!errorCode(error)) return null

  const message = errorBodyMessage(error)
  return message ? { body: message } : null
}
